

import { useCallback, useMemo, useState } from "react";
import type {
  ApproveOrderPaymentRequest,
  OrderPaymentProofUrlItem,
  PaymentReviewRejectReasonCode,
  RejectOrderPaymentRequest,
  SellerOrderDetail,
} from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoreOrderByIdQuery } from "@/modules/stores/services/queries/useStoreOrderByIdQuery";
import { useStoreOrderProofQuery } from "@/modules/stores/services/queries/useStoreOrderProofQuery";
import { useApproveStoreOrderPaymentMutation } from "@/modules/stores/services/mutations/useApproveStoreOrderPaymentMutation";
import { useRejectStoreOrderPaymentMutation } from "@/modules/stores/services/mutations/useRejectStoreOrderPaymentMutation";

type Props = {
  isOpen: boolean;
  storeId: string;
  orderId: string | null;
  onSuccess: (order: SellerOrderDetail) => void;
};

function parseMutationErrorCode(caughtError: unknown): string {
  if (!(caughtError instanceof ApiJsonClientError)) {
    return "UNKNOWN";
  }
  if (caughtError.code) {
    return caughtError.code;
  }
  if (caughtError.status === 409) {
    return "STALE_STATUS";
  }
  if (caughtError.message.trim().length > 0) {
    return caughtError.message.trim();
  }
  return "UNKNOWN";
}

export function usePaymentReviewState({
  isOpen,
  storeId,
  orderId,
  onSuccess,
}: Props) {
  const activeOrderId = isOpen && orderId ? orderId : "";
  const orderQuery = useStoreOrderByIdQuery({
    storeId,
    orderId: activeOrderId,
  });
  const proofQuery = useStoreOrderProofQuery({
    storeId,
    orderId: activeOrderId,
  });
  const { mutateAsync: approveOrderPayment } = useApproveStoreOrderPaymentMutation();
  const { mutateAsync: rejectOrderPayment } = useRejectStoreOrderPaymentMutation();

  // Local state holds user input and mutation results only; everything else
  // is derived from the queries so no sync-state effects are needed.
  const [orderOverride, setOrderOverride] = useState<SellerOrderDetail | null>(null);
  const [selectedProofIndex, setSelectedProofIndex] = useState(0);
  // null = untouched; the buyer-entered amount is used until the admin types.
  const [approvedAmountDraft, setApprovedAmountDraft] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState<PaymentReviewRejectReasonCode | "">(
    "",
  );
  const [note, setNote] = useState("");
  const [isSubmittingApprove, setIsSubmittingApprove] = useState(false);
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);
  const [actionErrorCode, setActionErrorCode] = useState<string | null>(null);
  const [mutationConflict, setMutationConflict] = useState(false);

  // Reset local state whenever the dialog target changes (open, close, or a
  // different order) — adjust-state-during-render pattern instead of effects.
  const [prevActiveOrderId, setPrevActiveOrderId] = useState(activeOrderId);
  if (activeOrderId !== prevActiveOrderId) {
    setPrevActiveOrderId(activeOrderId);
    setOrderOverride(null);
    setSelectedProofIndex(0);
    setApprovedAmountDraft(null);
    setReasonCode("");
    setNote("");
    setActionErrorCode(null);
    setMutationConflict(false);
    setIsSubmittingApprove(false);
    setIsSubmittingReject(false);
  }

  const loadedOrder = activeOrderId && !orderQuery.error ? (orderQuery.data ?? null) : null;
  const order = orderOverride ?? loadedOrder;
  const isProofNotFound =
    proofQuery.error instanceof ApiJsonClientError && proofQuery.error.status === 404;
  const proofItems: OrderPaymentProofUrlItem[] =
    activeOrderId && !proofQuery.error ? (proofQuery.data?.items ?? []) : [];
  const hasLoadError =
    Boolean(activeOrderId) &&
    (Boolean(orderQuery.error) || Boolean(proofQuery.error && !isProofNotFound));
  const conflictFromLoad = Boolean(
    !orderOverride &&
      loadedOrder &&
      loadedOrder.status !== "pending_payment_verification",
  );
  const isConflict = mutationConflict || conflictFromLoad;
  const errorCode =
    actionErrorCode ??
    (hasLoadError
      ? "PAYMENT_REVIEW_LOAD_ERROR"
      : conflictFromLoad
        ? "STALE_STATUS"
        : null);

  const hasProof = proofItems.length > 0;
  const buyerEnteredAmount = order?.paidAmount ?? 0;
  const requiredAmount = order?.requiredAmount ?? 0;
  // Default the approved amount to what the order expects — the deposit when
  // there is one, otherwise the (discounted) order total — not the amount the
  // buyer entered, so the reviewer can't one-click approve an over/under
  // payment. Matches the seller twin's expected-amount logic.
  const approvedAmountInput =
    approvedAmountDraft ??
    (order
      ? String(order.requiredAmount > 0 ? order.requiredAmount : order.totalAmount)
      : "");
  const approvedAmount = Number(approvedAmountInput.trim());
  const hasValidApprovedAmount =
    approvedAmountInput.trim().length > 0 &&
    Number.isFinite(approvedAmount) &&
    approvedAmount >= 0;

  const hasUnsavedChanges = useMemo(() => {
    if (!order) {
      return false;
    }
    if (approvedAmountInput.trim() !== String(order.paidAmount)) {
      return true;
    }
    if (reasonCode !== "") {
      return true;
    }
    return note.trim().length > 0;
  }, [approvedAmountInput, note, order, reasonCode]);

  const submitApprove = useCallback(async () => {
    if (!orderId || !hasValidApprovedAmount) {
      setActionErrorCode("INVALID_APPROVED_AMOUNT");
      return false;
    }
    if (!hasProof) {
      setActionErrorCode("PAYMENT_PROOF_REQUIRED");
      return false;
    }
    if (!storeId) {
      setActionErrorCode("UNKNOWN");
      return false;
    }
    setIsSubmittingApprove(true);
    setActionErrorCode(null);
    try {
      const payload: ApproveOrderPaymentRequest = {
        approvedAmount,
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      const updated = await approveOrderPayment({
        storeId,
        orderId,
        ...payload,
      });
      setOrderOverride(updated);
      onSuccess(updated);
      return true;
    } catch (caughtError) {
      const code = parseMutationErrorCode(caughtError);
      setActionErrorCode(code);
      if (code === "STALE_STATUS") {
        setMutationConflict(true);
      }
      return false;
    } finally {
      setIsSubmittingApprove(false);
    }
  }, [
    approveOrderPayment,
    approvedAmount,
    hasProof,
    hasValidApprovedAmount,
    note,
    onSuccess,
    orderId,
    storeId,
  ]);

  const submitReject = useCallback(async () => {
    if (!orderId) {
      return false;
    }
    if (!reasonCode) {
      setActionErrorCode("PAYMENT_REJECT_REASON_REQUIRED");
      return false;
    }
    if (!storeId) {
      setActionErrorCode("UNKNOWN");
      return false;
    }
    setIsSubmittingReject(true);
    setActionErrorCode(null);
    try {
      const payload: RejectOrderPaymentRequest = {
        reasonCode,
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      const updated = await rejectOrderPayment({
        storeId,
        orderId,
        ...payload,
      });
      setOrderOverride(updated);
      onSuccess(updated);
      return true;
    } catch (caughtError) {
      const code = parseMutationErrorCode(caughtError);
      setActionErrorCode(code);
      if (code === "STALE_STATUS") {
        setMutationConflict(true);
      }
      return false;
    } finally {
      setIsSubmittingReject(false);
    }
  }, [note, onSuccess, orderId, reasonCode, rejectOrderPayment, storeId]);

  const isLoading =
    Boolean(isOpen && orderId) && (orderQuery.isLoading || proofQuery.isLoading);
  const isSubmitting = isSubmittingApprove || isSubmittingReject;

  return {
    order,
    proofItems,
    selectedProofIndex,
    setSelectedProofIndex,
    approvedAmountInput,
    setApprovedAmountInput: setApprovedAmountDraft,
    reasonCode,
    setReasonCode,
    note,
    setNote,
    isLoading,
    isSubmitting,
    isConflict,
    errorCode,
    setErrorCode: setActionErrorCode,
    requiredAmount,
    buyerEnteredAmount,
    approvedAmount,
    hasProof,
    hasValidApprovedAmount,
    hasUnsavedChanges,
    submitApprove,
    submitReject,
  };
}
